interface DashIntegrationEventMap {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  KeyboardShortcutUpdated: {
    newShortcut: string;
  };
  SendMessageToRenderer: {
    message: MessagePayload;
  };
  SettingsUpdated: {
    updatedSettings: NativeSettings;
  };
}

interface DashIntegrationService {
  addEventListener: <T extends keyof DashIntegrationEventMap>(
    eventName: T,
    callback: (args: DashIntegrationEventMap[T]) => void,
  ) => () => void;
}

type MessageSender = "fss-launcher" | "fss-persistent-window";
type MessageRecipient = "fss-launcher" | "fss-persistent-window";
type MessageActionType = "no_op" | "navigate"; // TODO: add other action types here

export interface MessagePayload {
  sender: MessageSender;
  recipient: MessageRecipient;
  actionType: MessageActionType;
  payload: Record<string, unknown>;
}

export interface CurrentDesktopAccount {
  ui_flags: Record<string, unknown>;
  subscription_type: string;
  root_ns_id: number;
  root_ns_fileid: string;
  home_ns_id: number;
  home_ns_fileid: string;
  root_mount_path?: string;
  disable_multiaccount_link?: boolean;
  account_id: string;
  uid: number;
  email: string;
  email_verified: boolean;
  display_name: string;
  first_name?: string;
  role: "personal" | "business";
  team_id?: number;
  team_name?: string;
  host_nonce?: string;
  host_secret: string;
  host_int: number;
  client_uuid: string;
  name: {
    display_name: string;
  };
}

export interface NativeSettings {
  appShortcut?: string;
  persistentAppShortcut?: string;
}

export interface DashEsvController {
  getCurrentAccount: () => Promise<CurrentDesktopAccount>;
  reloadView: () => Promise<boolean>;
  showDashWindow: () => Promise<void>;
  getFeatureSessionId: () => Promise<string>;
  hideDashWindow: () => Promise<void>;
  launchPersistentWindow: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  getShortcutValue: () => Promise<string>;
  sendMessageToRenderer: (message: MessagePayload) => Promise<void>;
  getSettings: () => Promise<NativeSettings>;
  getViewName: () => Promise<string>;
}

export interface DashEsvControllerWithEventService extends DashEsvController {
  addDashEsvEventListener: DashIntegrationService["addEventListener"];
}

// Mirrors types in desktop/dropbox/views/common/shared/injected-types.ts
export const enum OsType {
  MAC = "mac",
  WIN = "win",
  LINUX = "linux",
}

interface FenixPlatform {
  config: {
    dropboxVersion: {
      buildKey: string;
      buildId: string;
      channel: string;
      versionNumber: string;
      isFrozen: boolean;
      isStoreManaged: boolean;
    };
    osPlatform: OsType;
  };
}

interface FenixWindow extends Window {
  controller: DashEsvController;
  fenixInitializationComplete: Promise<void>;
  platform: FenixPlatform;
  dashIntegrationService: DashIntegrationService;
}

export const getFenixPlatformConfig = (): FenixPlatform["config"] => {
  const fenixWindow = window as unknown as FenixWindow;
  return fenixWindow.platform.config;
};

export const PERSISTENT_WINDOW_VIEW_NAME = "dashView";

export type {
  DashIntegrationEventMap,
  DashIntegrationService,
  FenixPlatform,
  FenixWindow,
};

// These should be kept in sync with webapp RoutePath, but we don't want to
// import RoutePath from webapp because it's a circular dependency.
export const ROUTE_PATHS = {
  ROOT: "/",
  EMBED_LAUNCHER: "/embed/launcher",
  CHAT: "/chat",
};
